import React from 'react';
import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';

import { JobRemoteOptionValue } from '@/graphql/generated';

import { Props } from '../job-metadata';

export const StructuredData: React.FC<Props> = ({ job }) => {
  const company = job.companyInfo?.company;
  const showRating = company?.kununuData?.ratingAverage && company?.kununuData?.ratingCount;

  const aggregateRating = {
    '@type': 'AggregateRating',
    ratingValue: company?.kununuData?.ratingAverage,
    ratingCount: company?.kununuData?.ratingCount,
    worstRating: 1,
    bestRating: 5,
  };

  const hiringOrganization = {
    '@type': 'Organization',
    name: job.companyInfo?.companyNameOverride ?? company?.name,
    url: company?.links?.public,
    logo: company?.logo?.urlMeta,
    ...(showRating && { aggregateRating }),
  };

  const jobLocation = {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      streetAddress: job.location?.street,
      addressLocality: job.location?.city,
      addressRegion: job.location?.region,
      postalCode: job.location?.zipCode,
      addressCountry: job.location?.country?.countryCode,
    },
  };

  // Using https://developers.google.com/search/docs/data-types/job-posting#structured-data-type-definitions
  const jobPostingSchema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.title,
    description:
      job.description.__typename === 'HtmlDescription' ? job.description?.content : job.description?.htmlContent,
    datePosted: job.date,
    directApply: job.application?.__typename === 'JobXingApplication',
    validThrough: job.activeUntil,
    industry: job.industry?.title,
    employmentType: job.employmentType?.type,
    hiringOrganization,
    jobLocation,
    ...(job.remoteOptions?.includes(JobRemoteOptionValue.FullRemote)
      ? {
          jobLocationType: 'TELECOMMUTE',
          applicantLocationRequirements: {
            '@type': 'Country',
            name: job.location?.country?.localizationValue,
          },
        }
      : { jobLocationType: undefined }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{serialize(jobPostingSchema, { isJSON: true })}</script>;
    </Helmet>
  );
};
